<template>
  <a-table
    :rowKey="record => record.id"
    :columns="columns"
    :dataSource="data"
    :scroll="{ x: 3000 }"
    bordered
  >
    <template slot="Serial">{{ $t('business.label.serial') }}</template>
    <template slot="Uom">{{ $t('business.label.uom') }}</template>
    <template slot="Factor">{{ $t('business.label.factor') }}</template>
    <template slot="InnerFactor">{{ $t('business.label.innerFactor') }}</template>
    <template slot="Length">{{ $t('business.label.length') }}</template>
    <template slot="Width">{{ $t('business.label.width') }}</template>
    <template slot="Height">{{ $t('business.label.height') }}</template>
    <template slot="Volume">{{ $t('business.label.volume') }}</template>
    <template slot="Weight">{{ $t('business.label.weight') }}</template>
    <template slot="Specification">{{ $t('business.label.specification') }}</template>
    <template slot="Item.name">{{ $t('business.label.itemName') }}</template>
    <template slot="Barcode">{{ $t('business.label.barcode') }}</template>
    <template slot="Ordinal">{{ $t('business.label.ordinal') }}</template>
    <template slot="Master">{{ $t('business.label.master') }}</template>
    <template slot="Wrapping">{{ $t('business.label.wrapping') }}</template>
    <template slot="CatchWeight">{{ $t('business.label.catchWeight') }}</template>
    <template slot="AutoPalletization">{{ $t('business.label.autoPalletization') }}</template>


    <span slot="serial" slot-scope="text, record, index">
      {{ index + 1 }}
    </span>
    <span slot="master" slot-scope="text">
      <a-checkbox :checked="text" />
    </span>
    <span slot="wrapping" slot-scope="text">
      <a-checkbox :checked="text" />
    </span>
    <span slot="catchWeight" slot-scope="text">
      <a-checkbox :checked="text" />
    </span>
    <span slot="autoPalletization" slot-scope="text">
      <a-checkbox :checked="text" />
    </span>


    <div
      slot="filterDropdown"
      slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
      style="padding: 8px"
    >
      <a-input
        v-ant-ref="c => searchInput = c"
        :value="selectedKeys[0]"
        @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
        @pressEnter="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
        style="width: 188px; margin-bottom: 8px; display: block;"
      />
      <a-button
        type="primary"
        @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
        icon="search"
        size="small"
        style="width: 90px; margin-right: 8px">{{ $t('button.query') }}
      </a-button>
      <a-button @click="() => handleReset(clearFilters)" size="small" style="width: 90px">{{ $t('button.reset') }}
      </a-button>
    </div>
    <a-icon
      slot="filterIcon"
      slot-scope="filtered"
      type="search"
      :style="{ color: filtered ? '#108ee9' : undefined }" />
    <template slot="search" slot-scope="text, record, index, column">
      <span v-if="searchText && searchedColumn === column.dataIndex">
        <template
          v-for="(fragment, i) in text.toString().split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
        >
          <mark
            v-if="fragment.toLowerCase() === searchText.toLowerCase()"
            :key="i"
            class="highlight"
          >{{ fragment }}</mark
          >
          <template
            v-else
          >{{ fragment }}</template
          >
        </template>
      </span>
      <template
        v-else
      >{{ text }}
      </template
      >
    </template>
  </a-table>
</template>

<script>
  import { findEnum, findOne } from '@/api/Api'
  import { exportToExcel } from '@/components/Excel/export/exportFunction'
  import { excelName, filterVal, headerData } from '@/api/dto_js/import/itemPackBaseData'
  import { nameToOrdinal, ordinalToName, transformData } from '@/api/dto_js/import/transform'
  import Vue from 'vue'
  import { WAREHOUSE } from '@/store/mutation-types'
  import { checkFieldType } from '@/utils/field-check'
  import { getWarehouse } from '@/utils/util'

  export default {
    name: 'ItemPackBaseData',
    data() {
      return {
        data: [],
        // 表头
        columns: [
          {
            scopedSlots: { customRender: 'serial', title: 'Serial' }
          },
          {
            dataIndex: 'item.name',
            scopedSlots: {
              customRender: 'search',
              title: 'Item.name',
              filterDropdown: 'filterDropdown',
              filterIcon: 'filterIcon'
            },
            onFilter: (value, record) => record.item.name.toString().toLowerCase().includes(value.toLowerCase()),
            onFilterDropdownVisibleChange: visible => {
              if (visible) {
                setTimeout(() => {
                  this.searchInput.focus()
                }, 0)
              }
            }
          },
          {
            dataIndex: 'uom',
            scopedSlots: { title: 'Uom' }
          },
          {
            dataIndex: 'factor',
            scopedSlots: { title: 'Factor' }
          },
          {
            dataIndex: 'innerFactor',
            scopedSlots: { title: 'InnerFactor' }
          },
          {
            dataIndex: 'specification',
            scopedSlots: { title: 'Specification' }
          },
          {
            dataIndex: 'length',
            scopedSlots: { title: 'Length' }
          },
          {
            dataIndex: 'width',
            scopedSlots: { title: 'Width' }
          },
          {
            dataIndex: 'height',
            scopedSlots: { title: 'Height' }
          },
          {
            dataIndex: 'volume',
            scopedSlots: { title: 'Volume' }
          },
          {
            dataIndex: 'weight',
            scopedSlots: { title: 'Weight' }
          },
          {
            dataIndex: 'barcode',
            scopedSlots: { title: 'Barcode' }
          },
          {
            dataIndex: 'ordinal',
            scopedSlots: { title: 'Ordinal' }
          },
          {
            dataIndex: 'master',
            scopedSlots: { customRender: 'master', title: 'Master' }
          },
          {
            dataIndex: 'wrapping',
            scopedSlots: { customRender: 'wrapping', title: 'Wrapping' }
          },
          {
            dataIndex: 'catchWeight',
            scopedSlots: { customRender: 'catchWeight', title: 'CatchWeight' }
          },
          {
            dataIndex: 'autoPalletization',
            scopedSlots: { customRender: 'autoPalletization', title: 'AutoPalletization' }
          }
        ],
        excelData: [],
        searchText: '',
        searchInput: null,
        searchedColumn: '',
        warehouse: ''
      }
    },
    mounted() {
      this.warehouse = getWarehouse()
    },
    methods: {
      getData(data) {
        this.data = data
      },
      handleSearch(selectedKeys, confirm, dataIndex) {
        confirm()
        this.searchText = selectedKeys[0]
        this.searchedColumn = dataIndex
      },
      handleReset(clearFilters) {
        clearFilters()
        this.searchText = ''
      },
      exportData() {
        const excelData = JSON.parse(JSON.stringify(this.data))
        for (let x = 0; x < excelData.length; x++) {
          if (excelData[x].item) {
            excelData[x].item = excelData[x].item.name
          }
        }
        exportToExcel(headerData, filterVal, excelData, excelName)
      },
      transformData(data) {
        const list = transformData(headerData, filterVal, data)
        // this.$message.info(JSON.stringify(list))
        for (let i = 0; i < list.length; i++) {
          if (list[i].item) {
            list[i].item = { name: list[i].item }
          }
        }
        this.data = list
      },
      save() {
        const arrItem = []
        this.data.forEach(item => {
          item['id'] = null
          item['version'] = 0

          const map = new Map()
          map.set('type', 'packPersister')

          map.set('dataId', item.item.name + '-' + item.specification)
          map.set('content', item)
          const returnValue = checkFieldType(map)
          arrItem.push(returnValue)
        })
        // this.$message.info(JSON.stringify(this.data))
        this.$emit('sendSave', arrItem)
      },
      clear() {
        this.data = []
      }
    }
  }
</script>

<style scoped>
  .highlight {
    background-color: rgb(255, 192, 105);
    padding: 0;
  }
</style>